import type { ApiError } from '../http/errors.ts';
import type { Comment, CommentPage } from '../model/comment.ts';
import type { QueryValue } from '../instance/urls.ts';

/**
 * §6/§8's comment list as a state machine: which request to make, what to do with the page that
 * comes back, and what the resulting list *means* when it is empty or short.
 *
 * It is in core, not in the screen, for 1d's reason — a decision table in an event handler is a
 * decision table nobody tests — and for a second one specific to this endpoint: **the interesting
 * behaviour here is all failure behaviour**, and failure behaviour that only exists inside a React
 * hook is behaviour Compose will have to re-derive from the symptoms.
 *
 * Nothing here fetches. The reducers take a page or a failure and return the next state; the
 * platform supplies the transport.
 */

export type CommentSort = 'top' | 'new';

export const COMMENT_SORTS: readonly CommentSort[] = ['top', 'new'];

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * The one request shape this endpoint has. A continuation carries **no video-level parameters** —
 * see {@link commentsQueryParams}, which is where the reason lives.
 */
export type CommentQuery =
  | { readonly kind: 'first'; readonly videoId: string; readonly sort: CommentSort }
  | { readonly kind: 'continuation'; readonly videoId: string; readonly token: string };

export function firstPageQuery(videoId: string, sort: CommentSort): CommentQuery {
  return { kind: 'first', videoId, sort };
}

export function continuationQuery(videoId: string, token: string): CommentQuery {
  return { kind: 'continuation', videoId, token };
}

/**
 * The query string for a request, with two measured traps encoded rather than commented.
 *
 * 1. **The parameter is `sort_by`, not `sort`.** Search uses `sort` and ignores `sort_by`; comments
 *    do the opposite (API reference §6/§7). Either mistake produces a control that silently does
 *    nothing, and a `200` does not report it.
 * 2. **A continuation request sends the sort nowhere.** The token carries its own ordering:
 *    handing a `new` continuation `sort_by=top` alongside it returns the *next `new` page* anyway
 *    (measured 2026-07-28). Sending it would therefore be a parameter that looks like it works and
 *    never does — and would suggest the sort can be changed mid-list, which it cannot.
 */
export function commentsQueryParams(query: CommentQuery): Record<string, QueryValue> {
  return query.kind === 'first' ? { sort_by: query.sort } : { continuation: query.token };
}

// ---------------------------------------------------------------------------
// Failures, in the terms this list cares about
// ---------------------------------------------------------------------------

/**
 * Three outcomes, because the UI owes the reader three different sentences.
 *
 * `absent` is not an error at all: `404 {"error":"Comments not found."}` is what every **live
 * stream** answers (6 of 6 sampled), since a live video has a chat rather than comments. R6 maps
 * that 404 to `video-unavailable`, which on the Watch page reads as "Video not found" — about the
 * video, which is playing fine. So the comment layer re-reads it before anyone renders it.
 */
export type CommentsFailure =
  /** The endpoint says this video has no comments to give. Never an error state. */
  | { readonly kind: 'absent' }
  /** Invidious threw parsing *this* video's comments in *this* order. Deterministic — see below. */
  | { readonly kind: 'upstream-broken' }
  | { readonly kind: 'error'; readonly retryable: boolean };

export function commentsFailureOf(error: ApiError): CommentsFailure {
  if (error.kind === 'upstream-broken') return { kind: 'upstream-broken' };
  // 404 here is "no comments", never "no video" — the endpoint is per-video and the video loaded.
  if (error.kind === 'video-unavailable') return { kind: 'absent' };
  return { kind: 'error', retryable: error.retryable };
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type RepliesStatus = 'collapsed' | 'loading' | 'open' | 'failed';

export interface CommentThread {
  readonly comment: Comment;
  /** Loaded so far, in arrival order. Empty until the thread is expanded — replies are never inlined. */
  readonly replies: readonly Comment[];
  /** The next page of *this thread's* replies; `null` once they are all in. */
  readonly continuation: string | null;
  readonly status: RepliesStatus;
}

export type CommentsPhase =
  | 'loading'
  | 'ready'
  /** Loaded successfully and there is genuinely nothing to show. */
  | 'empty'
  /** The endpoint has no comments for this video at all (live streams). */
  | 'absent'
  | 'failed';

export interface CommentsState {
  readonly videoId: string;
  /** What the reader asked for. */
  readonly requestedSort: CommentSort;
  /** What is actually on screen. Differs from `requestedSort` only after a forced fallback. */
  readonly sort: CommentSort;
  readonly threads: readonly CommentThread[];
  /** The next page of top-level comments; `null` at the end of the list. */
  readonly continuation: string | null;
  /** The API's own total, from the first page only. `null` on later pages and on a replies page. */
  readonly commentCount: number | null;
  readonly phase: CommentsPhase;
  /** True while a *further* page is in flight, which is not the same as the list being loading. */
  readonly loadingMore: boolean;
  /**
   * Set when the requested order could not be served and another was used instead. The UI must say
   * so: silently showing newest-first under a control that reads "Top comments" is a lie the reader
   * has no way to detect.
   */
  readonly sortFallbackFrom: CommentSort | null;
  /** Set when the *page* failed, for the retry affordance. Cleared by any successful page. */
  readonly failure: CommentsFailure | null;
  /** R4 — records the parser had to drop, kept countable rather than silently vanishing. */
  readonly dropped: number;
}

export const DEFAULT_COMMENT_SORT: CommentSort = 'top';

export function initialCommentsState(videoId: string, sort: CommentSort = DEFAULT_COMMENT_SORT): CommentsState {
  return {
    videoId,
    requestedSort: sort,
    sort,
    threads: [],
    continuation: null,
    commentCount: null,
    phase: 'loading',
    loadingMore: false,
    sortFallbackFrom: null,
    failure: null,
    dropped: 0,
  };
}

function threadOf(comment: Comment): CommentThread {
  return {
    comment,
    replies: [],
    // The parser only keeps a `replies` block that still has its token, so this is either a usable
    // continuation or no replies at all — never a count behind a button that cannot load anything.
    continuation: comment.replies?.continuation ?? null,
    status: 'collapsed',
  };
}

/** How many replies a thread promises but has not loaded. Drives "Show N replies" honestly. */
export function pendingReplyCount(thread: CommentThread): number | null {
  const promised = thread.comment.replies?.replyCount ?? null;
  if (promised === null) return null;
  return Math.max(0, promised - thread.replies.length);
}

// ---------------------------------------------------------------------------
// Top-level pages
// ---------------------------------------------------------------------------

/**
 * Append a page. `append: false` for the first page of a (re)load, which is also what a sort change
 * and a retry produce — the list starts again rather than growing a second ordering onto the first.
 *
 * **Deduplicated by `commentId`.** Not defensive dressing: a duplicate is a duplicate React key and
 * a duplicate row, and the only thing standing between the list and one is a token nobody controls.
 * A repeat also silently drops the *new* copy rather than replacing the old, so a thread that has
 * already been expanded keeps its loaded replies.
 */
export function commentsPageLoaded(
  state: CommentsState,
  page: CommentPage,
  options: { readonly append: boolean; readonly sort: CommentSort },
): CommentsState {
  const kept = options.append ? [...state.threads] : [];
  const seen = new Set(kept.map((thread) => thread.comment.commentId));

  for (const comment of page.comments) {
    if (seen.has(comment.commentId)) continue;
    seen.add(comment.commentId);
    kept.push(threadOf(comment));
  }

  const commentCount = page.commentCount ?? (options.append ? state.commentCount : null);

  return {
    ...state,
    sort: options.sort,
    sortFallbackFrom: options.sort === state.requestedSort ? null : state.requestedSort,
    threads: kept,
    continuation: page.continuation,
    commentCount,
    phase: kept.length === 0 ? 'empty' : 'ready',
    loadingMore: false,
    failure: null,
    dropped: (options.append ? state.dropped : 0) + page.dropped.length,
  };
}

/**
 * `sort` is the order that was *attempted*, which after a fallback is not `requestedSort`. Recording
 * it is what keeps "top failed, newest is on screen" distinguishable from "both failed".
 */
export function commentsPageFailed(
  state: CommentsState,
  failure: CommentsFailure,
  sort: CommentSort,
): CommentsState {
  // A failure while paging keeps what is already on screen: losing 60 loaded comments because the
  // 61st page timed out is a worse outcome than the failure itself.
  if (state.loadingMore) {
    return { ...state, loadingMore: false, failure };
  }
  return {
    ...state,
    sort,
    threads: [],
    continuation: null,
    phase: failure.kind === 'absent' ? 'absent' : 'failed',
    loadingMore: false,
    failure,
    dropped: 0,
  };
}

export function commentsLoadingMore(state: CommentsState): CommentsState {
  return { ...state, loadingMore: true, failure: null };
}

/**
 * A sort change is a fresh list, not a re-order of the one in hand: the API returns *pages*, and
 * the 40 comments already loaded are the first two pages of the old ordering, which are not the
 * first two of the new one.
 */
export function commentsSortSelected(state: CommentsState, sort: CommentSort): CommentsState {
  if (sort === state.requestedSort && sort === state.sort) return state;
  return { ...initialCommentsState(state.videoId, sort), commentCount: state.commentCount };
}

/**
 * **The slice's central decision, as one function.**
 *
 * `sort_by=top` — which is also the default — answers `500 Missing hash key: "commentRenderer"` for
 * a large minority of ordinary videos: 13 of 45 sampled off `/popular` on 2026-07-28, persistent
 * per video across repeat requests, and unaffected by every other parameter tried (`source`, `hl`,
 * `region`, `thin_mode`). **`sort_by=new` rescued all 13.** So the failure is per (video, order),
 * not per video, and the list has somewhere to go rather than nowhere.
 *
 * Hence: fall back once, `top` → `new`, and only on this failure. Never the other way round —
 * `new` was refused by nothing in the sample, so a fallback *from* it would be a rule with no
 * measurement behind it and a second request behind every real outage. It is also what makes the
 * rule **terminating** by construction rather than by a counter: the only edge in the graph runs
 * `top → new`, so a fallback cannot chain into a loop however the failures fall.
 *
 * Takes the order that was *attempted* rather than the state, so it stays a two-input decision
 * table. The caller must surface `sortFallbackFrom` afterwards: a silent substitution is how the
 * reader ends up believing they are reading YouTube's ranking when they are reading a timeline.
 */
export function commentsSortFallback(attempted: CommentSort, failure: CommentsFailure): CommentSort | null {
  if (failure.kind !== 'upstream-broken') return null;
  return attempted === 'top' ? 'new' : null;
}

/**
 * Is the list short of what the API said the video has, with no way to ask for the rest?
 *
 * This is the rescued case's honest report. A video whose `top` page throws answers `new` with, for
 * example, 15 comments, `commentCount: 52` and **no continuation** — Invidious truncates where it
 * would otherwise throw. Rendering 15 with no remark would claim the reader has seen the comments.
 */
export function commentsTruncated(state: CommentsState): boolean {
  if (state.phase !== 'ready' || state.continuation !== null) return false;
  if (state.commentCount === null) return false;
  return state.threads.length < state.commentCount;
}

/**
 * An *empty* list from a video whose comments Invidious could not parse is not an empty comment
 * section — it is a failed one wearing a 200. Distinguished so the UI can say which it is: the
 * fallback only ever runs when the instance has already thrown on this video.
 */
export function commentsUnreadable(state: CommentsState): boolean {
  return state.phase === 'empty' && state.sortFallbackFrom !== null;
}

// ---------------------------------------------------------------------------
// Replies — one level down, and paged in their own right
// ---------------------------------------------------------------------------

function mapThread(
  state: CommentsState,
  commentId: string,
  update: (thread: CommentThread) => CommentThread,
): CommentsState {
  let touched = false;
  const threads = state.threads.map((thread) => {
    if (thread.comment.commentId !== commentId) return thread;
    touched = true;
    return update(thread);
  });
  return touched ? { ...state, threads } : state;
}

/**
 * Collapse an open thread, or mark a closed one as loading. Returns the state *and* whether the
 * caller should now fetch — because "expand" means two different things depending on whether the
 * replies are already in hand, and deciding that in the click handler is how a thread ends up
 * re-fetching the pages it already has every time it is toggled.
 */
export function toggleReplies(
  state: CommentsState,
  commentId: string,
): { readonly state: CommentsState; readonly fetch: string | null } {
  const thread = state.threads.find((candidate) => candidate.comment.commentId === commentId);
  if (thread === undefined) return { state, fetch: null };

  if (thread.status === 'open' || thread.status === 'loading') {
    return { state: mapThread(state, commentId, (t) => ({ ...t, status: 'collapsed' })), fetch: null };
  }

  // Re-opening a thread whose replies are already loaded is free.
  if (thread.replies.length > 0) {
    return { state: mapThread(state, commentId, (t) => ({ ...t, status: 'open' })), fetch: null };
  }

  const token = thread.continuation;
  if (token === null) return { state, fetch: null };
  return { state: mapThread(state, commentId, (t) => ({ ...t, status: 'loading' })), fetch: token };
}

export function repliesLoading(state: CommentsState, commentId: string): CommentsState {
  return mapThread(state, commentId, (thread) => ({ ...thread, status: 'loading' }));
}

/**
 * A replies page is the *same shape* as a comment page, which is why one parser serves both. What
 * differs is size: measured pages of 10, 45 and 11 for one 66-reply thread, so a fixed page size
 * cannot be assumed anywhere and "is it all in?" is `continuation === null`, never arithmetic on
 * `replyCount`.
 *
 * YouTube nests exactly two levels and the API agrees — no reply in any sampled page carried a
 * `replies` block of its own — so a reply's own thread is not modelled. If one ever appears, it is
 * dropped here rather than rendered at a depth the design has no room for.
 */
export function repliesLoaded(state: CommentsState, commentId: string, page: CommentPage): CommentsState {
  return mapThread(state, commentId, (thread) => {
    const seen = new Set(thread.replies.map((reply) => reply.commentId));
    const replies = [...thread.replies];
    for (const reply of page.comments) {
      if (seen.has(reply.commentId)) continue;
      seen.add(reply.commentId);
      replies.push(reply);
    }
    return { ...thread, replies, continuation: page.continuation, status: 'open' };
  });
}

export function repliesFailed(state: CommentsState, commentId: string): CommentsState {
  return mapThread(state, commentId, (thread) => ({
    ...thread,
    // Keep whatever pages did arrive; a failed third page must not discard the first two.
    status: thread.replies.length > 0 ? 'open' : 'failed',
  }));
}
